import { isYandleAgentHostEnabled } from '@/utils/yandleAgentHost.js'
import { getYandleAgentTenantId } from '@/utils/yandleAgentTenant.js'

const CHAT_WS_BASE = 'wss://voice.yandle.io/chat'

/** @type {WebSocket | null} */
let socket = null
let liveSessionToken = 0
/** @type {((s: ChatSessionState) => void) | null} */
let deliverSession = null
/** @type {((line: ChatTranscriptLine) => void) | null} */
let deliverTranscript = null
/** @type {((active: boolean) => void) | null} */
let deliverTyping = null

let userEnded = false

/** @typedef {{ phase: string; title: string; subtitle?: string; orgName?: string }} ChatSessionState */
/** @typedef {{ role: 'user' | 'agent'; text: string; timestamp?: string }} ChatTranscriptLine */

function buildChatWsUrl(tenantId) {
  const id = encodeURIComponent(tenantId)
  return `${CHAT_WS_BASE}/${id}`
}

/** @param {ChatSessionState} state */
function emitSession(state) {
  deliverSession?.(state)
}

/** @param {ChatTranscriptLine} line */
function emitTranscript(line) {
  deliverTranscript?.(line)
}

/** @param {Record<string, unknown>} data */
function handleSocketMessage(data) {
  const type = typeof data?.type === 'string' ? data.type : ''

  if (type === 'status') {
    const msg = typeof data.message === 'string' ? data.message : ''
    const orgName = typeof data.org_name === 'string' ? data.org_name : ''

    if (msg === 'connected') {
      emitSession({
        phase: 'connected',
        title: 'Connected',
        subtitle: orgName
          ? `You're chatting with ${orgName}'s assistant.`
          : 'Send a message to start the conversation.',
        orgName: orgName || undefined,
      })
      return
    }
    if (msg) {
      emitSession({
        phase: 'session_update',
        title: 'Chat update',
        subtitle: msg,
        orgName: orgName || undefined,
      })
    }
    return
  }

  if (type === 'message') {
    return
  }

  if (type === 'transcript') {
    const role =
      data.role === 'agent' ? 'agent' : data.role === 'user' ? 'user' : null
    const text = typeof data.text === 'string' ? data.text.trim() : ''
    if (!role || !text) return
    const timestamp =
      typeof data.timestamp === 'string' ? data.timestamp : undefined
    emitTranscript({ role, text, timestamp })
    return
  }

  if (type === 'typing') {
    deliverTyping?.(data.active === true)
    return
  }
}

function attachSocketHandlers(ws, myToken) {
  ws.onmessage = (event) => {
    if (myToken !== liveSessionToken || userEnded) return
    try {
      const data = JSON.parse(event.data)
      handleSocketMessage(data)
    } catch (err) {
      console.error('[YandleChat]', err)
    }
  }

  ws.onerror = () => {
    if (myToken !== liveSessionToken || userEnded) return
    emitSession({
      phase: 'error',
      title: 'Connection error',
      subtitle: 'Chat could not stay connected. Try again.',
    })
  }

  ws.onclose = () => {
    if (myToken !== liveSessionToken) return
    socket = null
    if (userEnded) return
    emitSession({
      phase: 'error',
      title: 'Chat disconnected',
      subtitle: 'The connection closed. Start a new chat to continue.',
    })
  }
}

/**
 * @param {{ silent?: boolean }} [options]
 */
export function stopYandleChatSession(options = {}) {
  const { silent = false } = options
  userEnded = true
  if (socket) {
    socket.onclose = null
    socket.close()
    socket = null
  }
  if (!silent) {
    deliverSession?.({
      phase: 'chat_ended',
      title: 'Chat ended',
      subtitle: 'You can start a new chat anytime.',
    })
  }
  liveSessionToken += 1
  deliverSession = null
  deliverTranscript = null
  deliverTyping = null
}

/**
 * @param {{
 *   tenantId?: string
 *   onSessionUpdate?: (state: ChatSessionState) => void
 *   onTranscriptUpdate?: (line: ChatTranscriptLine) => void
 *   onTypingUpdate?: (active: boolean) => void
 * }} [options]
 */
export function startYandleChatSession(options = {}) {
  const { tenantId, onSessionUpdate, onTranscriptUpdate, onTypingUpdate } =
    options

  if (!isYandleAgentHostEnabled()) {
    return Promise.reject(
      new Error('Yandle chat is only available on yandle.io'),
    )
  }

  stopYandleChatSession({ silent: true })

  liveSessionToken += 1
  const myToken = liveSessionToken
  userEnded = false
  deliverSession = (state) => {
    if (myToken !== liveSessionToken) return
    onSessionUpdate?.(state)
  }
  deliverTranscript = (line) => {
    if (myToken !== liveSessionToken) return
    onTranscriptUpdate?.(line)
  }
  deliverTyping = (active) => {
    if (myToken !== liveSessionToken) return
    onTypingUpdate?.(active)
  }

  const resolvedTenantId = getYandleAgentTenantId({
    tenantId,
    channel: 'chat',
  })

  emitSession({
    phase: 'connecting',
    title: 'Connecting…',
    subtitle: 'Opening a secure chat with the AI assistant.',
  })

  return new Promise((resolve, reject) => {
    let settled = false
    const ws = new WebSocket(buildChatWsUrl(resolvedTenantId))
    socket = ws

    ws.onopen = () => {
      if (myToken !== liveSessionToken) return
      attachSocketHandlers(ws, myToken)
      emitSession({
        phase: 'connected',
        title: 'Connected',
        subtitle: 'Send a message to start the conversation.',
      })
      if (!settled) {
        settled = true
        resolve()
      }
    }

    ws.onerror = () => {
      if (myToken !== liveSessionToken) return
      if (!settled) {
        settled = true
        reject(new Error('Could not connect to Yandle chat'))
      }
    }

    ws.onclose = (event) => {
      if (myToken !== liveSessionToken) return
      if (!settled) {
        settled = true
        const reason =
          typeof event?.reason === 'string' && event.reason.trim()
            ? event.reason.trim()
            : 'Chat connection closed before it was ready'
        reject(new Error(reason))
      }
    }
  })
}

/**
 * @param {string} text
 */
export function sendYandleChatMessage(text) {
  const trimmed = typeof text === 'string' ? text.trim() : ''
  if (!trimmed) return false
  if (!socket || socket.readyState !== WebSocket.OPEN) return false

  socket.send(JSON.stringify({ type: 'message', text: trimmed }))
  return true
}

export function isYandleChatConnected() {
  return Boolean(socket && socket.readyState === WebSocket.OPEN)
}
